import React, { useEffect, useMemo, useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { TeacherRegistrationFormData, FormErrors } from '../types';
import { api } from '../../../lib/api';

type SchoolOption = { id: number; name: string; education_level?: string | null; school_mode?: 'b2c' | 'b2b' };

interface TeacherRegistrationFormProps {
  onSubmit: (data: TeacherRegistrationFormData) => Promise<void>;
  isLoading: boolean;
}

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;

const TeacherRegistrationForm: React.FC<TeacherRegistrationFormProps> = ({ onSubmit, isLoading }) => {
  const [formData, setFormData] = useState<TeacherRegistrationFormData>({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    county: '',
    city: '',
    school: '',
    schoolCustom: '',
    educationLevel: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [counties, setCounties] = useState<string[]>([]);
  const [localities, setLocalities] = useState<string[]>([]);
  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [isLoadingCounties, setIsLoadingCounties] = useState(false);
  const [isLoadingLocalities, setIsLoadingLocalities] = useState(false);
  const [isLoadingSchools, setIsLoadingSchools] = useState(false);

  const countyOptions = useMemo(
    () => counties.map((c) => ({ value: c, label: c })),
    [counties]
  );

  const localityOptions = useMemo(
    () => localities.map((l) => ({ value: l, label: l })),
    [localities]
  );

  const schoolOptions = useMemo(() => {
    const opts = schools.map((s) => ({ value: s.name, label: s.name }));
    return [...opts, { value: '__custom__', label: 'Creează școală nouă' }];
  }, [schools]);

  const selectedSchoolMode = useMemo(() => {
    if (!formData.school || formData.school === '__custom__') return null;
    const match = schools.find((s) => String(s.name) === String(formData.school));
    return (match?.school_mode || 'b2c') as 'b2c' | 'b2b';
  }, [formData.school, schools]);
  const isB2BSchoolSelected = selectedSchoolMode === 'b2b';

  const educationLevelOptions = useMemo(
    () => [
      { value: 'gimnazial', label: 'Școală gimnazială' },
      { value: 'liceal', label: 'Liceu' },
      { value: 'universitar', label: 'Universitate' },
    ],
    []
  );

  useEffect(() => {
    const loadCounties = async () => {
      setIsLoadingCounties(true);
      try {
        const resp = await api.get('/api/locatii/counties');
        setCounties(resp.data || []);
      } finally {
        setIsLoadingCounties(false);
      }
    };
    void loadCounties();
  }, []);

  useEffect(() => {
    const loadLocalities = async () => {
      if (!formData.county) {
        setLocalities([]);
        setSchools([]);
        return;
      }
      setIsLoadingLocalities(true);
      try {
        const resp = await api.get('/api/locatii/localities', { params: { county: formData.county } });
        setLocalities(resp.data || []);
      } finally {
        setIsLoadingLocalities(false);
      }
    };
    void loadLocalities();
  }, [formData.county]);

  useEffect(() => {
    const loadSchools = async () => {
      if (!formData.county || !formData.city) {
        setSchools([]);
        return;
      }
      setIsLoadingSchools(true);
      try {
        const resp = await api.get('/api/locatii/schools', {
          params: { county: formData.county, city: formData.city },
        });
        setSchools(resp.data || []);
      } finally {
        setIsLoadingSchools(false);
      }
    };
    void loadSchools();
  }, [formData.county, formData.city]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.fullName.trim()) newErrors.fullName = 'Numele este obligatoriu';

    if (!formData.email.trim()) newErrors.email = 'Emailul este obligatoriu';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = 'Introduceți un email valid';

    if (!formData.password) newErrors.password = 'Parola este obligatorie';
    else if (!PASSWORD_REGEX.test(formData.password))
      newErrors.password =
        'Minim 8 caractere, o literă mare, una mică, o cifră și un simbol';

    if (!formData.confirmPassword) newErrors.confirmPassword = 'Confirmă parola';
    else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Parolele nu coincid';

    if (!formData.county) newErrors.county = 'Selectează județ';
    if (!formData.city) newErrors.city = 'Selectează localitate';

    if (!formData.school) newErrors.school = 'Selectează școala';
    if (formData.school === '__custom__' && !formData.schoolCustom.trim())
      newErrors.schoolCustom = 'Introdu numele școlii';

    if (isB2BSchoolSelected) {
      newErrors.school =
        'Pe școala selectată există un abonament B2B. Pentru înregistrare, luați legătura cu secretariatul.';
    }

    // Education level is only needed when creating a new school.
    if (formData.school === '__custom__' && !formData.educationLevel) newErrors.educationLevel = 'Selectează nivelul';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      await onSubmit(formData);
    }
  };

  const setField = <K extends keyof TeacherRegistrationFormData>(field: K, value: TeacherRegistrationFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="p-3 bg-muted rounded-md border border-border">
        <p className="text-xs text-foreground">
          Doar cadrele didactice pot crea conturi și școli.
        </p>
      </div>

      <Input
        type="text"
        label="Nume complet"
        placeholder="Ex: Popescu Ion"
        value={formData.fullName}
        onChange={(e) => setField('fullName', e.target.value)}
        error={errors.fullName}
        required
        disabled={isLoading}
      />

      <Input
        type="email"
        label="Email"
        placeholder="exemplu@domeniu.ro"
        value={formData.email}
        onChange={(e) => setField('email', e.target.value)}
        error={errors.email}
        required
        disabled={isLoading}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative">
          <Input
            type={showPassword ? 'text' : 'password'}
            label="Parolă"
            placeholder="Parolă"
            value={formData.password}
            onChange={(e) => setField('password', e.target.value)}
            error={errors.password}
            required
            disabled={isLoading}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-colors duration-150"
            aria-label={showPassword ? 'Ascunde parola' : 'Arată parola'}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
          </button>
        </div>
        <div className="relative">
          <Input
            type={showConfirmPassword ? 'text' : 'password'}
            label="Confirmă parola"
            placeholder="Confirmă parola"
            value={formData.confirmPassword}
            onChange={(e) => setField('confirmPassword', e.target.value)}
            error={errors.confirmPassword}
            required
            disabled={isLoading}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-colors duration-150"
            aria-label={showConfirmPassword ? 'Ascunde parola' : 'Arată parola'}
          >
            <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={18} />
          </button>
        </div>
      </div>

      <div className="pt-4 border-t border-border">
        <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="School" size={18} className="text-primary" />
          Date școală
        </h3>

        <div className="space-y-4">
          <Select
            label="Județ"
            placeholder="Selectează județ"
            options={countyOptions}
            value={formData.county}
            onChange={(v) => {
              setField('county', String(v));
              setField('city', '');
              setField('school', '');
              setField('schoolCustom', '');
            }}
            error={errors.county}
            required
            disabled={isLoading}
            loading={isLoadingCounties}
            searchable
            clearable
          />

          <Select
            label="Localitate"
            placeholder="Selectează localitate"
            options={localityOptions}
            value={formData.city}
            onChange={(v) => {
              setField('city', String(v));
              setField('school', '');
              setField('schoolCustom', '');
            }}
            error={errors.city}
            required
            disabled={isLoading || !formData.county}
            loading={isLoadingLocalities}
            searchable
            creatable
            createLabel={(term) => `Folosește localitatea: "${term}"`}
            clearable
          />

          <Select
            label="Școală"
            placeholder="Selectează școala"
            options={schoolOptions}
            value={formData.school}
            onChange={(v) => {
              const sel = String(v);
              setField('school', sel);
              if (sel !== '__custom__') {
                setField('schoolCustom', '');
                const match = schools.find((s) => String(s.name) === sel);
                const lvl = (match?.education_level || '').toString().trim();
                setField('educationLevel', lvl || '');
              } else {
                // New school: ask explicitly for level
                setField('educationLevel', '');
              }
            }}
            error={errors.school}
            description="Verifică dacă școala există deja în listă înainte să creezi una nouă."
            required
            disabled={isLoading || !formData.county || !formData.city}
            loading={isLoadingSchools}
            searchable
            clearable
          />

          {isB2BSchoolSelected && (
            <div className="text-sm bg-warning/10 border border-warning/20 text-warning rounded-md p-3">
              Pe școala selectată există un abonament B2B. Pentru înregistrare, luați legătura cu secretariatul.
            </div>
          )}

          {formData.school === '__custom__' && (
            <>
              <div className="p-3 bg-warning/10 border border-warning/20 rounded-md">
                <p className="text-xs text-foreground">
                  Atenție: verifică lista de școli de mai sus înainte să creezi una nouă (poate există deja).
                </p>
              </div>
              <Input
                type="text"
                label="Nume școală"
                placeholder="Nume școală"
                value={formData.schoolCustom}
                onChange={(e) => setField('schoolCustom', e.target.value)}
                error={errors.schoolCustom}
                required
                disabled={isLoading}
              />
            </>
          )}

          {formData.school === '__custom__' ? (
            <Select
              label="Nivel învățământ"
              placeholder="Selectează nivel"
              options={educationLevelOptions}
              value={formData.educationLevel}
              onChange={(v) => setField('educationLevel', String(v))}
              error={errors.educationLevel}
              required
              disabled={isLoading}
            />
          ) : formData.educationLevel ? (
            <div className="text-xs text-muted-foreground">
              Nivel învățământ: <span className="text-foreground font-medium">{formData.educationLevel}</span> (preluat din școală)
            </div>
          ) : null}
        </div>
      </div>

      <Button
        type="submit"
        variant="default"
        size="lg"
        fullWidth
        loading={isLoading}
        disabled={isLoading || isB2BSchoolSelected}
        iconName="UserPlus"
        iconPosition="right"
      >
        Creează cont profesor
      </Button>
    </form>
  );
};

export default TeacherRegistrationForm;
